// INSTRUCTIONS: 
// This is your COMPLETE UPDATED api.js file with search methods added.
// Replace your entire frontend/src/services/api.js with this file.

// frontend/src/services/api.js

import axios from 'axios';
import logger from '../utils/logger';
import { videosAPI } from './api/videos';

// Constants
const TOKEN_KEY = 'ZToken';

const API_BASE_URL = 'https://z-poel.onrender.com/api/v1';

const IS_DEBUG = 
                 import.meta.env.VITE_DEBUG_API === 'true' || 
                 import.meta.env.MODE === 'development';

if (IS_DEBUG) {
    console.log(`[apiService] Initializing with API_BASE_URL: ${API_BASE_URL}`);
    console.log(`[apiService] Debug mode: ${IS_DEBUG}`);
}

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000,
  withCredentials: true
});

// Helper Functions
const logRequest = (config) => {
  if (!IS_DEBUG) return config;
  const { method, baseURL, url, params, data, headers } = config;
  console.debug(`[API Interceptor] Request: ${method?.toUpperCase()} ${baseURL}${url}`);
  if (params && Object.keys(params).length > 0) console.debug(`[API Interceptor] Request Params:`, params);
  if (data && typeof data === 'object' && Object.keys(data).length > 0) {
      if (data instanceof FormData) {
          console.debug(`[API Interceptor] Request Data (Payload type): FormData`);
          for (let pair of data.entries()) {
            console.debug(`  - ${pair[0]}: ${typeof pair[1] === 'object' ? '[File/Blob]' : pair[1]}`);
          }
      } else {
          console.debug(`[API Interceptor] Request Data (Payload):`, data);
      }
  } else if (data) {
      console.debug(`[API Interceptor] Request Data (Payload type): ${typeof data}`);
  }
  console.debug(`[API Interceptor] Request Headers (Content-Type): ${headers['Content-Type'] || 'Not Set Automatically'}`);
  return config;
};

const logResponse = (response) => {
  if (!IS_DEBUG) return response;
  const { config, status, data } = response;
  console.debug(`[API Interceptor] Response from ${config.method?.toUpperCase()} ${config.url}: Status ${status}`, data);
  return response;
};

const logError = (error) => {
  if (!IS_DEBUG) return Promise.reject(error);
  const { config, response, message } = error;
  const method = config?.method?.toUpperCase();
  const url = config?.url;
  const status = response?.status || 'N/A';
  console.error(
    `[API Interceptor] Error from ${method} ${url}: Status ${status} - ${message}`,
    { responseData: response?.data, requestConfig: config }
  );
  return Promise.reject(error);
};

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(TOKEN_KEY) || localStorage.getItem('token');
    if (token) {
      if (IS_DEBUG) console.debug(`[API Interceptor] Attaching Bearer token to request for ${config.url}.`);
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      if (IS_DEBUG) console.warn(`[API Interceptor] No token found. Request to ${config.url} is unauthenticated.`);
    }
    return logRequest(config);
  },
  (error) => {
    console.error('[API Interceptor] Request setup error:', error);
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => {
    return logResponse(response);
  },
  (error) => {
    if (error.response?.status === 401) {
      console.warn('[API Interceptor] Received 401 Unauthorized. Clearing tokens.');
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    return logError(error);
  }
);

const createApiMethod = (method, urlTemplate, isBodyRequest = false, paramNameInPath) => {
  return async (firstArg, secondArg, thirdArg) => {
    let fullUrl = urlTemplate;
    let requestBody = {};
    let requestParams = {};
    let finalAxiosConfig = thirdArg || {};

    if (paramNameInPath) {
      const pathParam = firstArg;
      if (pathParam === undefined || pathParam === null || pathParam === '') {
        throw new Error(`${paramNameInPath} is required for ${method.toUpperCase()} ${urlTemplate}`);
      }
      fullUrl = urlTemplate.replace(`:${paramNameInPath}`, encodeURIComponent(pathParam));
      
      if (isBodyRequest) { 
        requestBody = secondArg || {};
        finalAxiosConfig = thirdArg || {}; 
      } else { 
        requestParams = secondArg || {};
        finalAxiosConfig = thirdArg || {};
      }
    } else { 
      if (isBodyRequest) { 
        requestBody = firstArg || {};
        finalAxiosConfig = secondArg || {}; 
      } else { 
        requestParams = firstArg || {};
        finalAxiosConfig = secondArg || {};
      }
    }

    const recoContext = finalAxiosConfig._recoContext || {};
    delete finalAxiosConfig._recoContext;

    if (method.toLowerCase() === 'post' || method.toLowerCase() === 'put' || method.toLowerCase() === 'patch' || 
        (method.toLowerCase() === 'delete' && isBodyRequest)) {
      if (requestBody instanceof FormData) {
        Object.keys(recoContext).forEach(key => {
          requestBody.append(`reco_${key}`, recoContext[key]);
        });
      } else {
        requestBody = { ...requestBody, ...recoContext };
      }
    } else {
      requestParams = { ...requestParams, ...recoContext };
    }

    try {
      let response;
      const axiosRequestConfig = {
        params: requestParams,
        ...finalAxiosConfig
      };

      switch (method.toLowerCase()) {
        case 'get':
          response = await apiClient.get(fullUrl, axiosRequestConfig);
          break;
        case 'post':
          response = await apiClient.post(fullUrl, requestBody, axiosRequestConfig);
          break;
        case 'put':
          response = await apiClient.put(fullUrl, requestBody, axiosRequestConfig);
          break;
        case 'patch':
          response = await apiClient.patch(fullUrl, requestBody, axiosRequestConfig);
          break;
        case 'delete':
          response = await apiClient.delete(fullUrl, isBodyRequest ? { data: requestBody, ...axiosRequestConfig } : axiosRequestConfig);
          break;
        default:
          throw new Error(`Unsupported HTTP method: ${method}`);
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  };
};

// Auth Methods
const authMethods = {
  register: (userData) => apiClient.post('/auth/register', userData).then(res => res.data),
  login: (credentials) => apiClient.post('/auth/login', credentials).then(res => res.data),
  logout: async () => {
    try { 
      await apiClient.post('/auth/logout'); 
    } catch (error) { 
      if (IS_DEBUG) console.error('[apiService - logout] Backend logout API call failed. This is often okay.', error.message); 
    }
  },
  getCurrentAuthenticatedUser: () => apiClient.get('/auth/me').then(res => res.data),
  getProfile: () => apiClient.get('/auth/profile').then(res => res.data),
  updateProfile: (data) => apiClient.patch('/auth/profile', data).then(res => res.data),
  verifyEmail: (token) => {
    if (!token || typeof token !== 'string' || token.trim() === '') {
      const msg = '[apiService - verifyEmail] Token is required and must be a non-empty string.';
      console.error(msg);
      return Promise.reject(new Error(msg));
    }
    return apiClient.get(`/auth/verify-email/${encodeURIComponent(token)}`).then(res => res.data);
  },
  resendVerificationEmail: (payload) => apiClient.post('/auth/resend-verification', payload).then(res => res.data),
  requestPasswordReset: (email) => apiClient.post('/auth/request-password-reset', { email }).then(res => res.data),
  resetPassword: (payload) => apiClient.post('/auth/reset-password', payload).then(res => res.data),
};

// Posts Methods
const postsMethods = {
  create: (data) => {
    const formData = new FormData();
    formData.append('content', data.content);
    if (data.image) {
      formData.append('image', data.image);
    }
    return apiClient.post('/posts', formData).then(res => res.data);
  },
  
  getAll: (page = 1, limit = 10) => 
    apiClient.get(`/posts?page=${page}&limit=${limit}`).then(res => res.data),
  
  getOne: (id) => apiClient.get(`/posts/${id}`).then(res => res.data),
  
  update: (id, data) => {
    const formData = new FormData();
    formData.append('content', data.content);
    if (data.image) {
      formData.append('image', data.image);
    }
    return apiClient.patch(`/posts/${id}`, formData).then(res => res.data);
  },
  
  delete: (id) => apiClient.delete(`/posts/${id}`).then(res => res.data),
  
  getAllPosts: createApiMethod('GET', '/posts', false),
  getRecentPosts: createApiMethod('GET', '/posts/recent', false),
  getPostById: createApiMethod('GET', '/posts/:postId', false, 'postId'),
  createPost: createApiMethod('POST', '/posts', true),
  updatePost: createApiMethod('PUT', '/posts/:postId', true, 'postId'),
  deletePost: createApiMethod('DELETE', '/posts/:postId', false, 'postId'),
  
  likePost: createApiMethod('POST', '/posts/:postId/like', true, 'postId'), 
  unlikePost: createApiMethod('DELETE', '/posts/:postId/like', false, 'postId'),
  addComment: createApiMethod('POST', '/posts/:postId/comments', true, 'postId'),
  getComments: createApiMethod('GET', '/posts/:postId/comments', false, 'postId'),
  recordDetailedView: createApiMethod('POST', '/posts/:postId/engagement', true, 'postId'),
  
  addReaction: createApiMethod('POST', '/posts/:postId/reactions', true, 'postId'), 
  removeReaction: createApiMethod('DELETE', '/posts/:postId/reactions', true, 'postId'), 
  getPostReactions: createApiMethod('GET', '/posts/:postId/reactions', false, 'postId'),
  
  getUserPosts: (userId, page = 1, limit = 10) => 
    apiClient.get(`/posts/user/${userId}?page=${page}&limit=${limit}`).then(res => res.data),
  fetchUserPosts: createApiMethod('GET', '/users/:username/posts', false, 'username'),
  fetchUserMedia: createApiMethod('GET', '/users/:username/media', false, 'username'),
  fetchUserLikedPosts: createApiMethod('GET', '/users/:username/liked-posts', false, 'username'),
  
  trackView: createApiMethod('POST', '/posts/:postId/view', false, 'postId'),
  
  toggleBookmark: createApiMethod('POST', '/posts/:postId/bookmark', false, 'postId'),
};

// Comments Methods
const commentsMethods = {
  create: (postId, data) => apiClient.post(`/comments/posts/${postId}`, data).then(res => res.data),
  getAll: (postId, page = 1, limit = 10) => 
    apiClient.get(`/comments/posts/${postId}?page=${page}&limit=${limit}`).then(res => res.data),
  update: (id, data) => apiClient.patch(`/comments/${id}`, data).then(res => res.data),
  delete: (id) => apiClient.delete(`/comments/${id}`).then(res => res.data),
  
  getCommentById: createApiMethod('GET', '/comments/:commentId', false, 'commentId'),
  likeComment: createApiMethod('POST', '/comments/:commentId/like', false, 'commentId'),
  unlikeComment: createApiMethod('DELETE', '/comments/:commentId/like', false, 'commentId'),
  getReplies: createApiMethod('GET', '/comments/:commentId/replies', false, 'commentId'),
  addReply: createApiMethod('POST', '/comments/:commentId/replies', true, 'commentId')
};

// Likes Methods
const likesMethods = {
  toggle: (postId) => apiClient.post(`/likes/posts/${postId}`).then(res => res.data),
  getAll: (postId) => apiClient.get(`/likes/posts/${postId}`).then(res => res.data),
  check: (postId) => apiClient.get(`/likes/posts/${postId}/check`).then(res => res.data),
  getLikeCount: createApiMethod('GET', '/likes/posts/:postId/count', false, 'postId'),
  getUserLikes: createApiMethod('GET', '/likes/user/:userId', false, 'userId')
};

// Media Methods
const mediaMethods = {
  upload: createApiMethod('POST', '/media/upload', true),
};

// Categories Methods
const categoriesMethods = {
  getCategories: createApiMethod('GET', '/categories', false),
  getCategoryById: createApiMethod('GET', '/categories/:categoryId', false, 'categoryId'),
};

// Users Methods
const usersMethods = {
  updateUserProfile: (userId, formData, axiosConfig = {}) => {
    const recoContext = axiosConfig._recoContext || {};
    Object.keys(recoContext).forEach(key => {
      formData.append(`reco_${key}`, recoContext[key]);
    });
    delete axiosConfig._recoContext;

    return apiClient.put(`/users/${userId}`, formData, axiosConfig).then(res => res.data);
  },
  
  searchUsers: createApiMethod('GET', '/users/search', false),
  getUserProfile: createApiMethod('GET', '/users/:username', false, 'username'),
  
  getUserById: createApiMethod('GET', '/users/id/:userId', false, 'userId'),
  
  getUserProfileById: async (userId) => {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }
      const response = await apiClient.get(`/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('[apiService] Error fetching user profile by ID:', error);
      throw error;
    }
  },
  
  updateProfile: async (updateData) => {
    try {
      const response = await apiClient.put('/users/profile', updateData);
      return response.data;
    } catch (error) {
      console.error('[apiService] Error updating profile:', error);
      throw error;
    }
  },
  
  followUser: createApiMethod('POST', '/users/:userId/follow', false, 'userId'),
  unfollowUser: createApiMethod('DELETE', '/users/:userId/follow', false, 'userId'),
  getPopularUsers: createApiMethod('GET', '/users/popular', false),
  muteContent: createApiMethod('POST', '/users/me/mute', true),
  unmuteContent: createApiMethod('DELETE', '/users/me/mute', false),
  blockUser: createApiMethod('POST', '/users/:userId/block', true, 'userId'), 
  unblockUser: createApiMethod('DELETE', '/users/:userId/block', false, 'userId'),
};

// Feeds Methods
const feedsMethods = {
  getGlobalFeed: createApiMethod('GET', '/feeds/global', false),
  getPersonalizedFeed: createApiMethod('GET', '/feeds/personalized', false),
  getTrendingFeed: createApiMethod('GET', '/feeds/trending', false),
  getLocalFeed: createApiMethod('GET', '/feeds/local', false),
};

// Notifications Methods
const notificationsMethods = {
  getNotifications: createApiMethod('GET', '/notifications', false),
  markNotificationAsRead: createApiMethod('PUT', '/notifications/:notificationId/read', false, 'notificationId'),
  markAllNotificationsAsRead: createApiMethod('PUT', '/notifications/read-all', false), 
  getUnreadCount: createApiMethod('GET', '/notifications/unread-count', false),
};

// Admin Methods
const adminMethods = {
  getSystemStats: createApiMethod('GET', '/admin/stats', false),
  getReports: createApiMethod('GET', '/admin/reports', false),
  manageUser: createApiMethod('PUT', '/admin/users/:userId/manage', true, 'userId'),
};

// Chat Methods
const chatMethods = {
  getOrCreateConversationWithUser: createApiMethod('GET', '/chat/conversations/direct/:recipientId', false, 'recipientId'),
  getConversationDetails: createApiMethod('GET', '/chat/conversations/:conversationId', false, 'conversationId'),
  sendMessage: createApiMethod('POST', '/chat/conversations/:conversationId/messages', true, 'conversationId'),
  getMessages: createApiMethod('GET', '/chat/conversations/:conversationId/messages', false, 'conversationId'),
  markConversationAsRead: createApiMethod('PUT', '/chat/conversations/:conversationId/read', false, 'conversationId'),
  getConversations: createApiMethod('GET', '/chat/conversations', false),
};

// Recommendation Methods
const recommendationMethods = {
  getPersonalizedPosts: createApiMethod('GET', '/recommendations/posts/forUser', false),
  getTrendingPosts: createApiMethod('GET', '/recommendations/posts/trending', false),
  getWhoToFollow: createApiMethod('GET', '/recommendations/users/whoToFollow', false),
  getSimilarPosts: createApiMethod('GET', '/recommendations/posts/:postId/similar', false, 'postId'),
  getPersonalizedCategories: createApiMethod('GET', '/recommendations/categories/personalized', false),
};

// Meetings Methods
const meetingsMethods = {
  getMeetings: createApiMethod('GET', '/meetings', false),
  createRequest: createApiMethod('POST', '/meetings/request', true),
  updateStatus: createApiMethod('PATCH', '/meetings/:meetingId/status', true, 'meetingId'),
  reschedule: createApiMethod('PATCH', '/meetings/:meetingId/reschedule', true, 'meetingId'),
  shareLiveLocation: createApiMethod('POST', '/meetings/:meetingId/location', true, 'meetingId'),
  getParticipantLocation: createApiMethod('GET', '/meetings/:meetingId/participant-location', false, 'meetingId'),
  shareLocationWithTrustedContacts: createApiMethod('POST', '/meetings/safety/share-location', true),
  getPopularVenues: createApiMethod('GET', '/meetings/venues', false),
  getSafeMeetingPoints: createApiMethod('POST', '/meetings/safe-points', true),
  checkIn: createApiMethod('POST', '/meetings/:meetingId/check-in', false, 'meetingId'),
  reportConcern: createApiMethod('POST', '/meetings/:meetingId/report', true, 'meetingId'),
  blockUserFromMeetings: createApiMethod('POST', '/meetings/block', true),
  getBlockedUsers: createApiMethod('GET', '/meetings/blocked-users', false),
  getUserMeetingHistory: createApiMethod('GET', '/meetings/history/:userId', false, 'userId'),
  rateMeeting: createApiMethod('POST', '/meetings/:meetingId/rate', true, 'meetingId'),
  getSuggestions: createApiMethod('GET', '/meetings/suggestions/:userId', false, 'userId'),
};

// ✅ NEW: Search Methods
const searchMethods = {
  // Global search across all categories
  globalSearch: createApiMethod('GET', '/search/global', false),
  
  // Category-specific searches
  searchPosts: createApiMethod('GET', '/search/posts', false),
  searchVoiceNotes: createApiMethod('GET', '/search/voice-notes', false),
  searchEvents: createApiMethod('GET', '/search/events', false),
  searchMeetings: createApiMethod('GET', '/search/meetings', false),
  searchTasks: createApiMethod('GET', '/search/tasks', false),
  searchItems: createApiMethod('GET', '/search/items', false),
  searchLocations: createApiMethod('GET', '/search/locations', false),
  searchUsers: createApiMethod('GET', '/search/users', false),
  
  // Advanced search with filters
  advancedSearch: createApiMethod('POST', '/search/advanced', true),
};

// WanderMatch Dating Methods
const wanderMatchDatingMethods = {
  createDatingProfile: createApiMethod('POST', '/wandermatch/dating/profile', true),
  getDatingProfile: createApiMethod('GET', '/wandermatch/dating/profile/:userId', false, 'userId'),
  updateDatingProfile: createApiMethod('PUT', '/wandermatch/dating/profile', true),
  uploadDatingPhotos: createApiMethod('POST', '/wandermatch/dating/profile/photos', true),
  deleteDatingPhoto: createApiMethod('DELETE', '/wandermatch/dating/profile/photos/:photoId', false, 'photoId'),
  verifyProfile: createApiMethod('POST', '/wandermatch/dating/profile/verify', true),
  getVerificationStatus: createApiMethod('GET', '/wandermatch/dating/profile/verification', false),
  getSwipeStack: createApiMethod('GET', '/wandermatch/dating/stack', false),
  swipe: createApiMethod('POST', '/wandermatch/dating/swipe', true),
  rewind: createApiMethod('POST', '/wandermatch/dating/rewind', false),
  getSwipeHistory: createApiMethod('GET', '/wandermatch/dating/swipe-history', false),
  getMatches: createApiMethod('GET', '/wandermatch/dating/matches', false),
  getMatch: createApiMethod('GET', '/wandermatch/dating/matches/:matchId', false, 'matchId'),
  unmatch: createApiMethod('DELETE', '/wandermatch/dating/matches/:matchId', false, 'matchId'),
  blockMatch: createApiMethod('POST', '/wandermatch/dating/matches/:matchId/block', false, 'matchId'),
  reportMatch: createApiMethod('POST', '/wandermatch/dating/matches/:matchId/report', true, 'matchId'),
  sendDatingMessage: createApiMethod('POST', '/wandermatch/dating/matches/:matchId/messages', true, 'matchId'),
  getDatingMessages: createApiMethod('GET', '/wandermatch/dating/matches/:matchId/messages', false, 'matchId'),
  markDatingChatAsRead: createApiMethod('PUT', '/wandermatch/dating/matches/:matchId/read', false, 'matchId'),
  scheduleVideoDate: createApiMethod('POST', '/wandermatch/dating/video-dates', true),
  getVideoDates: createApiMethod('GET', '/wandermatch/dating/video-dates', false),
  startVideoCall: createApiMethod('POST', '/wandermatch/dating/video-dates/:callId/start', false, 'callId'),
  endVideoCall: createApiMethod('POST', '/wandermatch/dating/video-dates/:callId/end', false, 'callId'),
  getVideoToken: createApiMethod('GET', '/wandermatch/dating/video-dates/:callId/token', false, 'callId'),
  getDateIdeas: createApiMethod('GET', '/wandermatch/dating/date-ideas', false),
  suggestDateIdea: createApiMethod('POST', '/wandermatch/dating/date-ideas', true),
  saveDate: createApiMethod('POST', '/wandermatch/dating/dates', true),
  getUpcomingDates: createApiMethod('GET', '/wandermatch/dating/dates/upcoming', false),
  purchaseBoost: createApiMethod('POST', '/wandermatch/dating/boost', true),
  purchaseSuperLikes: createApiMethod('POST', '/wandermatch/dating/super-likes/purchase', true),
  getBoostStatus: createApiMethod('GET', '/wandermatch/dating/boost/status', false),
  getSuperLikesRemaining: createApiMethod('GET', '/wandermatch/dating/super-likes/remaining', false),
  getDatingPreferences: createApiMethod('GET', '/wandermatch/dating/preferences', false),
  updateDatingPreferences: createApiMethod('PUT', '/wandermatch/dating/preferences', true),
  getDatingStats: createApiMethod('GET', '/wandermatch/dating/stats', false),
  getProfileViews: createApiMethod('GET', '/wandermatch/dating/profile/views', false),
};

// WanderMatch Travel Methods
const wanderMatchTravelMethods = {
  createTravelProfile: createApiMethod('POST', '/wandermatch/travel/profile', true),
  getTravelProfile: createApiMethod('GET', '/wandermatch/travel/profile/:userId', false, 'userId'),
  updateTravelProfile: createApiMethod('PUT', '/wandermatch/travel/profile', true),
  createTrip: createApiMethod('POST', '/wandermatch/travel/trips', true),
  getTrips: createApiMethod('GET', '/wandermatch/travel/trips', false),
  getTrip: createApiMethod('GET', '/wandermatch/travel/trips/:tripId', false, 'tripId'),
  updateTrip: createApiMethod('PUT', '/wandermatch/travel/trips/:tripId', true, 'tripId'),
  deleteTrip: createApiMethod('DELETE', '/wandermatch/travel/trips/:tripId', false, 'tripId'),
  joinTrip: createApiMethod('POST', '/wandermatch/travel/trips/:tripId/join', false, 'tripId'),
  leaveTrip: createApiMethod('POST', '/wandermatch/travel/trips/:tripId/leave', false, 'tripId'),
  findTravelBuddies: createApiMethod('GET', '/wandermatch/travel/buddies', false),
  sendBuddyRequest: createApiMethod('POST', '/wandermatch/travel/buddy-requests', true),
  getBuddyRequests: createApiMethod('GET', '/wandermatch/travel/buddy-requests', false),
  respondToBuddyRequest: createApiMethod('PUT', '/wandermatch/travel/buddy-requests/:requestId', true, 'requestId'),
  searchFlights: createApiMethod('GET', '/wandermatch/travel/flights/search', false),
  getFlightDetails: createApiMethod('GET', '/wandermatch/travel/flights/:flightId', false, 'flightId'),
  bookFlight: createApiMethod('POST', '/wandermatch/travel/flights/book', true),
  getFlightBookings: createApiMethod('GET', '/wandermatch/travel/bookings/flights', false),
  cancelFlightBooking: createApiMethod('POST', '/wandermatch/travel/bookings/flights/:bookingId/cancel', false, 'bookingId'),
  searchHotels: createApiMethod('GET', '/wandermatch/travel/hotels/search', false),
  getHotelDetails: createApiMethod('GET', '/wandermatch/travel/hotels/:hotelId', false, 'hotelId'),
  checkHotelAvailability: createApiMethod('GET', '/wandermatch/travel/hotels/:hotelId/availability', false, 'hotelId'),
  bookHotel: createApiMethod('POST', '/wandermatch/travel/hotels/book', true),
  getHotelBookings: createApiMethod('GET', '/wandermatch/travel/bookings/hotels', false),
  cancelHotelBooking: createApiMethod('POST', '/wandermatch/travel/bookings/hotels/:bookingId/cancel', false, 'bookingId'),
  searchCars: createApiMethod('GET', '/wandermatch/travel/cars/search', false),
  bookCar: createApiMethod('POST', '/wandermatch/travel/cars/book', true),
  getCarBookings: createApiMethod('GET', '/wandermatch/travel/bookings/cars', false),
  searchActivities: createApiMethod('GET', '/wandermatch/travel/activities/search', false),
  getActivityDetails: createApiMethod('GET', '/wandermatch/travel/activities/:activityId', false, 'activityId'),
  bookActivity: createApiMethod('POST', '/wandermatch/travel/activities/book', true),
  addExpense: createApiMethod('POST', '/wandermatch/travel/expenses', true),
  getExpenses: createApiMethod('GET', '/wandermatch/travel/expenses', false),
  updateExpense: createApiMethod('PUT', '/wandermatch/travel/expenses/:expenseId', true, 'expenseId'),
  deleteExpense: createApiMethod('DELETE', '/wandermatch/travel/expenses/:expenseId', false, 'expenseId'),
  getExpenseReport: createApiMethod('GET', '/wandermatch/travel/trips/:tripId/expense-report', false, 'tripId'),
  createSplitRequest: createApiMethod('POST', '/wandermatch/travel/expenses/split', true),
  getSplitRequests: createApiMethod('GET', '/wandermatch/travel/expenses/splits', false),
  settleSplit: createApiMethod('POST', '/wandermatch/travel/expenses/splits/:splitId/settle', true, 'splitId'),
  getPopularDestinations: createApiMethod('GET', '/wandermatch/travel/destinations/popular', false),
  getDestinationDetails: createApiMethod('GET', '/wandermatch/travel/destinations/:destinationId', false, 'destinationId'),
  getDestinationGuides: createApiMethod('GET', '/wandermatch/travel/destinations/:destinationId/guides', false, 'destinationId'),
  addPlaceReview: createApiMethod('POST', '/wandermatch/travel/reviews', true),
  getPlaceReviews: createApiMethod('GET', '/wandermatch/travel/places/:placeId/reviews', false, 'placeId'),
  uploadDocument: createApiMethod('POST', '/wandermatch/travel/documents', true),
  getDocuments: createApiMethod('GET', '/wandermatch/travel/documents', false),
  deleteDocument: createApiMethod('DELETE', '/wandermatch/travel/documents/:documentId', false, 'documentId'),
  createItinerary: createApiMethod('POST', '/wandermatch/travel/itineraries', true),
  getItinerary: createApiMethod('GET', '/wandermatch/travel/itineraries/:tripId', false, 'tripId'),
  updateItinerary: createApiMethod('PUT', '/wandermatch/travel/itineraries/:tripId', true, 'tripId'),
  shareItinerary: createApiMethod('POST', '/wandermatch/travel/itineraries/:tripId/share', true, 'tripId'),
};
// Resources Methods
const resourcesMethods = {
  getAll: createApiMethod('GET', '/resources', false),
  getById: createApiMethod('GET', '/resources/:id', false, 'id'),
  create: createApiMethod('POST', '/resources', true),
  update: createApiMethod('PUT', '/resources/:id', true, 'id'),
  delete: createApiMethod('DELETE', '/resources/:id', false, 'id'),
  requestResource: createApiMethod('POST', '/resources/:id/request', true, 'id'),
  getMyRequests: createApiMethod('GET', '/resources/my-requests', false),
  getResourceRequests: createApiMethod('GET', '/resources/:id/requests', false, 'id'),
  acceptRequest: createApiMethod('POST', '/resources/:resourceId/requests/:requestId/accept', false, 'resourceId'),
  declineRequest: createApiMethod('POST', '/resources/:resourceId/requests/:requestId/decline', false, 'resourceId'),
  getOnMap: createApiMethod('GET', '/resources/map', false),
};

// ✅ UPDATED: Main API Service Object with search added
const apiService = {
  auth: authMethods,
  posts: postsMethods,
  comments: commentsMethods,
  likes: likesMethods,
  media: mediaMethods,
  categories: categoriesMethods,
  users: usersMethods,
  feeds: feedsMethods,
  notifications: notificationsMethods,
  admin: adminMethods,
  chat: chatMethods,
  recommendations: recommendationMethods,
  meetings: meetingsMethods,
  search: searchMethods,
  resources: resourcesMethods,  // ✅ ADD THIS LINE
  videos: videosAPI,  // ← ADD THIS LINE


  
  wanderMatchDating: wanderMatchDatingMethods,
  wanderMatchTravel: wanderMatchTravelMethods,
  
  setAuthToken: (token) => {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem('token', token);
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem('token');
      delete apiClient.defaults.headers.common['Authorization'];
    }
    if (IS_DEBUG) console.info(`[apiService - setAuthToken] Token ${token ? 'set' : 'cleared'}.`);
  },
  
  clearAuthToken: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('token');
    delete apiClient.defaults.headers.common['Authorization'];
    if (IS_DEBUG) console.info(`[apiService - clearAuthToken] Token cleared.`);
  }
};

export { apiClient };
export const api = apiClient;
export const authAPI = authMethods;
export const postsAPI = postsMethods;
export const commentsAPI = commentsMethods;
export const likesAPI = likesMethods;
export const usersAPI = usersMethods;
export const datingAPI = wanderMatchDatingMethods;
export const travelAPI = wanderMatchTravelMethods;
export { videosAPI };  // ✅ Re-export the imported videosAPI

export default apiService;
